import React, { useContext } from 'react'

import classNames from 'classnames'
import PropTypes from 'prop-types'
import { Reference } from 'react-popper'

import DropdownContext from '../../utils/DropdownContext'
import Button from '../Button'
import styling from './robitDropdown.module.scss'

export const DropdownToggle = ({
  caret,
  className,
  children,
  disabled,
  nav,
  onClick,
  split,
  styleType = 'primary',
  tag,
  useANormalRef = false,
  ...rest
}) => {
  const context = useContext(DropdownContext)

  const handleClick = e => {
    if (rest.disabled) {
      e.preventDefault()
      return
    }

    if (nav && !tag) {
      e.preventDefault()
    }

    if (onClick) {
      onClick(e)
    }

    context.toggle(e)
  }

  const handleKeyDown = e => {
    if (rest.disabled) {
      e.preventDefault()
      return
    }

    if (nav && !tag) {
      e.preventDefault()
    }

    if ((e.key === 'ArrowDown' || e.key === 'ArrowUp') && !context.open) context.toggle(e)
  }

  const ariaLabel = rest['aria-label'] || 'Toggle Dropdown'
  const classes = classNames(
    className,
    ((caret && !disabled) || split) && styling['dropdown-toggle'],
    disabled && styling['dropdown-disabled'],
    split && styling['dropdown-toggle-split'],
    rest.size && styling[rest.size],
    nav && styling['nav-link'],
    'px-3 md:px-5'
  )

  const dropdownChildren = children || <span className='sr-only'>{ariaLabel}</span>

  let Tag

  if (nav && !tag) {
    Tag = 'a'
    rest.href = '#'
  }
  else if (!tag) {
    Tag = Button
    rest.styleType = styleType
  }
  else {
    Tag = tag
  }

  if (context.inNavbar) {
    return (
      <Reference>
        {({ ref }) => (
          <Tag
            innerRef={ref}
            {...rest}
            aria-expanded={context.open}
            className={classes}
            disabled={disabled}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
          >
            {dropdownChildren}
          </Tag>
        )}
      </Reference>
    )
  }

  return (
    <Reference>
      {({ ref }) => (
        <Tag
          disabled={disabled}
          innerRef={!useANormalRef ? ref : undefined}
          ref={useANormalRef ? ref : undefined}
          {...rest}
          aria-expanded={context.open}
          className={classes}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
        >
          {dropdownChildren}
          {caret && <div className='inline-block w-6' />}
        </Tag>
      )}
    </Reference>
  )
}

DropdownToggle.propTypes = {
  /**
   * Whether it should display a caret, or not.
   */
  caret: PropTypes.bool,
  /**
   * The children nodes.
   */
  children: PropTypes.node,
  /**
   * The class name.
   */
  className: PropTypes.string,
  /**
   * Whether it is disabled, or not.
   */
  disabled: PropTypes.bool,
  /**
   * Whether it is located inside a nav, or not.
   */
  nav: PropTypes.bool,
  /**
   * The function that should be triggered on click.
   */
  onClick: PropTypes.func,
  /**
   * Whether it is split trigger, or not.
   */
  split: PropTypes.bool,
  /**
   * The styleType color. Defaults to 'primary'
   */
  styleType: PropTypes.string,
  /**
   * The component's tag type.
   */
  tag: PropTypes.oneOfType([PropTypes.func, PropTypes.string]),
  /**
   * The dropdown toggle requires the attachment of a ref. If the Tag is a Robits component, it should use the defined `innerRef` of that passed in Tag. Otherwise, you HAVE to set this value as TRUE, so that it falls back to a normal `ref` attribute. Defaults to false.
   */
  useANormalRef: PropTypes.bool,
}

export default DropdownToggle
